import React, { FC, PropsWithChildren } from 'react';
import { Grid, Tooltip } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Truck, Paperclip, Download } from 'react-bootstrap-icons';

import { useGetOrder, useGetPanelOrder } from 'api';
import { Status } from 'components/controls';
import classnames from 'classnames';
import { format } from 'date-fns';

import { useAppNavigate, useRWD, useNotifications } from 'hooks';

import styles from 'theme/pages/Order/components/OrderSummary/OrderSummary.module.scss';

interface IProps {
  orderId: number;
  isPanel: boolean;
}

const OrderSummary: FC<IProps> = ({ orderId, isPanel }) => {
  const navigate = useAppNavigate();
  const { showErrorMessage } = useNotifications();
  const { t } = useTranslation();

  const { data: orderData, isLoading } = (isPanel ? useGetPanelOrder : useGetOrder)(orderId, {
    onSuccess(data) {
      if (!data.id && data.message) {
        navigate('/dashboard/orders');
        showErrorMessage(data.message);
      }
    }
  });

  const { isMobile } = useRWD();

  // Załączniki dropshippingowe (etykieta kuriera, protokół nadania).
  // Pokazujemy niezależnie od flagi dropshipping - same w sobie sygnalizują własną wysyłkę.
  const dropshippingAttachments = orderData?.dropshipping_attachments ?? [];
  const isDropshipping = orderData?.dropshipping === true;
  const hasAttachments = dropshippingAttachments.length > 0;
  const showDropshippingBlock = isDropshipping || hasAttachments;

  const attachmentTypeLabel = (type: string) =>
    type === 'dropshipping_label' ? t('Etykieta kuriera') : t('Protokół nadania');

  const Box: FC<PropsWithChildren> = ({ children }) => <div className={styles.box}>{children}</div>;

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Pages-Order-components-OrderSummary'
      )}>
      {isMobile && <div className={classnames(styles.title, styles.mainTitle)}>ID{orderId}D</div>}
      <div className={styles.blocks}>
        <Grid container className={styles['summary-blocks']}>
          <Grid item md={8} className={styles['summary-entities']}>
            {!isMobile && (
              <div className={styles.title}>
                <Trans>Zamówienie</Trans>: {orderData?.name}
              </div>
            )}

            {!isLoading && (
              <div className={styles.company}>
                <div className={styles.customer}>
                  <div className={styles.title}>
                    <Trans>Dane płatnika</Trans>:
                  </div>
                  {orderData?.client_name}
                  <br />
                  {orderData?.client_address.street}
                  <br />
                  {orderData?.client_address.postal_code} {orderData?.client_address.city}
                  <br />
                  <br />
                  <span className={styles.label}>
                    <Trans>NIP</Trans>:
                  </span>{' '}
                  {orderData?.client_tax_number}
                  <br />
                  <span className={styles.label}>
                    <Trans>E-mail</Trans>:
                  </span>{' '}
                  {orderData?.client_email}
                  <br />
                  <span className={styles.label}>
                    <Trans>Telefon</Trans>:
                  </span>{' '}
                  {orderData?.client_phone}
                </div>

                <div className={styles.receiver}>
                  <div className={styles.title}>
                    <Trans>Dane odbiorcy</Trans>:
                  </div>
                  {orderData?.receiver_name}
                  <br />
                  {orderData?.receiver_email}
                  <br />
                  <br />
                  <span className={styles.label}>
                    <Trans>Telefon</Trans>:
                  </span>{' '}
                  {orderData?.receiver_phone}
                </div>
              </div>
            )}
          </Grid>

          <Grid item md={4} className={styles['summary-status']}>
            <Box>
              <div className={styles.name}>
                <Trans>Data utworzenia</Trans>
              </div>
              <div className={classnames(styles.value, { [styles.largeValue]: isMobile })}>
                {orderData?.create_date
                  ? format(new Date(orderData.create_date), 'dd-MM-yyyy')
                  : '---'}
              </div>
            </Box>

            <Box>
              <div className={styles.name}>
                <Trans>Status</Trans>
              </div>
              <div className={styles.statusWrapper}>
                <Status
                  icon={orderData?.status.icon || ''}
                  message={orderData?.status.label || ''}
                  color={orderData?.status.color}
                />
              </div>
            </Box>

            <Box>
              <div className={styles.name}>
                <Trans>Wartość zamówienia</Trans>
              </div>
              {orderData?.value_net_formatted ? (
                <>
                  <div className={styles.value}>
                    <span>
                      <strong className={styles.large}>
                        {orderData?.value_net_formatted} {orderData?.currency}
                      </strong>
                    </span>
                    <Trans>netto</Trans>
                  </div>
                  <div className={styles.value}>
                    <span>
                      {orderData?.value_gross_formatted} {orderData?.currency}
                    </span>{' '}
                    <Trans>brutto</Trans>
                  </div>
                </>
              ) : (
                '---'
              )}
            </Box>
          </Grid>
        </Grid>
      </div>

      {showDropshippingBlock && (
        <div
          style={{
            margin: '16px 0',
            padding: '16px',
            background: '#0d94880D',
            borderLeft: '3px solid #0d9488',
            borderRadius: 4
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: hasAttachments ? 12 : 0,
              flexWrap: 'wrap'
            }}>
            {isDropshipping && (
              <Tooltip title={t('Zamówienie dropshipping')} arrow>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    background: '#0d9488',
                    color: '#fff'
                  }}>
                  <Truck style={{ fontSize: 14 }} />
                  <Trans>Dropshipping</Trans>
                </span>
              </Tooltip>
            )}
            {hasAttachments && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#0d9488'
                }}>
                <Paperclip />
                <Trans>Załączniki własnej wysyłki</Trans> ({dropshippingAttachments.length})
              </span>
            )}
          </div>
          {hasAttachments && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {dropshippingAttachments.map((att) => (
                <li
                  key={att.id}
                  style={{
                    padding: '8px 0',
                    borderTop: '1px solid #0d948833',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}>
                  <span style={{ minWidth: 140, fontWeight: 600, color: '#475569' }}>
                    {attachmentTypeLabel(att.type)}:
                  </span>
                  <span style={{ flex: 1, color: '#334155' }}>
                    {att.name || att.original_filename || `#${att.id}`}
                  </span>
                  {att.url ? (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        background: '#0d9488',
                        color: '#fff',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        textDecoration: 'none'
                      }}>
                      <Download style={{ fontSize: 12 }} />
                      <Trans>Pobierz</Trans>
                    </a>
                  ) : (
                    <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                      <Trans>Brak pliku</Trans>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className={styles.list}>
        <div className={classnames(styles.name, styles.historyName, { [styles.small]: isMobile })}>
          <Trans>Historia zmian</Trans>:
        </div>
        <div className={styles.history}>
          {orderData?.status_history.map((item, index) => (
            <div className={styles.event} key={`${item.date}-${item.hour}-${index}`}>
              <div className={styles.date}>
                {format(new Date(item.date + ' ' + item.hour), 'dd.MM.yyyy')}{' '}
                <span>{format(new Date(item.date + ' ' + item.hour), '(H:mm)')}</span>
              </div>{' '}
              - {item.status_message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
